import { useEffect, useState } from "react";
import { MessageSquareText } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import WhatsAppIcon from "../../components/ui/WhatsAppIcon";
import api from "../../api";

// NL Labs: which text channel a message tries first (backend
// notifications/messaging_switches.py, system_settings MESSAGING_PREFERRED_CHANNEL).
// Each message goes as one text, never both; the other channel is the fallback.
const OPTIONS = [
  { value: "whatsapp", label: "WhatsApp first, then SMS", hint: "Cheaper per message. Patients who haven't agreed to WhatsApp get SMS." },
  { value: "sms", label: "SMS first, then WhatsApp", hint: "Use while the WhatsApp account is low on balance or has a problem." },
];

const CHANNEL = {
  sms: { label: "SMS", icon: <MessageSquareText className="w-4 h-4 text-secondary-600" /> },
  whatsapp: { label: "WhatsApp", icon: <WhatsAppIcon size={16} brand /> },
};
const STATUS = {
  ready: ["Ready", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  disabled: ["Template off", "bg-amber-50 text-amber-900 border-amber-200"],
  no_template_id: ["No template id", "bg-amber-50 text-amber-900 border-amber-200"],
  no_gateway: ["Not set up on server", "bg-amber-50 text-amber-900 border-amber-200"],
  channel_off: ["Switched off", "bg-secondary-50 text-secondary-600 border-secondary-200"],
  unknown_flow: ["No template", "bg-secondary-50 text-secondary-600 border-secondary-200"],
};
const AUDIENCE = { patient: "Patient", staff: "Staff", clinic: "Clinic" };

function StatusPill({ status }) {
  if (status === "not_used") return <span className="text-xs text-secondary-400">Not used</span>;
  const [label, cls] = STATUS[status] || [status, "bg-secondary-50 text-secondary-600 border-secondary-200"];
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${cls}`}>{label}</span>;
}

function Route({ m }) {
  if (!m.order.length) return <span className="text-sm text-secondary-500">No text (app and email only)</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-sm text-secondary-900">
      {m.order.map((c, i) => (
        <span key={c} className="flex items-center gap-1">
          {i > 0 && <span className="text-xs text-secondary-500">then</span>}
          {CHANNEL[c].icon} {CHANNEL[c].label}
        </span>
      ))}
      {m.whatsapp_on_request && <span className="text-xs text-secondary-500">(WhatsApp if asked)</span>}
      {m.order.length === 1 && !m.whatsapp_on_request && <span className="text-xs text-secondary-500">only</span>}
    </span>
  );
}

function MessageMap({ data }) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-secondary-900">Where each message goes</h2>
        <p className="text-sm text-secondary-500 mt-1">
          The order a message tries, and whether each channel's template is ready. Patient messages also need the
          clinic's alert channels (Admin → Organizations → Features) and its own switch for that alert (Settings →
          Patient alerts). WhatsApp to a patient goes only if they agreed to WhatsApp messages; otherwise the next
          channel is used.
        </p>
      </div>
      {data.groups.map((g) => {
        const rows = data.messages.filter((m) => m.group === g.key);
        if (!rows.length) return null;
        return (
          <div key={g.key} className="rounded-xl border border-secondary-200 bg-white overflow-hidden">
            <h3 className="px-4 py-2.5 bg-secondary-50 text-sm font-semibold text-secondary-800">{g.label}</h3>
            <ul className="divide-y divide-secondary-100">
              {rows.map((m) => (
                <li key={m.flow_key} className="px-4 py-3 grid gap-2 md:grid-cols-[1fr_auto] md:items-start">
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-semibold text-secondary-900">
                      {m.label}
                      <span className="ml-2 text-xs font-normal text-secondary-500">to {AUDIENCE[m.audience] || m.audience}</span>
                      {!m.live && <span className="ml-2 text-xs font-normal text-secondary-500">(not sent by the app yet)</span>}
                    </p>
                    <Route m={m} />
                    {m.used_for.length > 1 && <p className="text-xs text-secondary-500">Used for: {m.used_for.join(", ")}</p>}
                    {m.note && <p className="text-xs text-secondary-500">{m.note}</p>}
                    {m.live && !m.can_send && m.order.length > 0 && (
                      <p className="text-xs text-amber-800">Nothing will send: no channel in its order is ready.</p>
                    )}
                  </div>
                  <div className="flex md:flex-col gap-3 md:gap-1 md:items-end">
                    {["sms", "whatsapp"].map((c) => (
                      <span key={c} className="flex items-center gap-1.5">
                        {CHANNEL[c].icon}<StatusPill status={m.status[c]} />
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

export default function AdminMessageChannels() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.fetchMessagingSwitches()
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.data?.error || "Couldn't load message channels."));

  useEffect(() => { load(); }, []);

  const switches = data?.switches;

  const preferred = switches?.preferred || "whatsapp";

  const choose = async (value) => {
    if (value === preferred) return;
    setBusy(true);
    setError(null);
    try {
      await api.saveMessagingPreferred(value);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8 max-w-4xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Message Channels</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every alert, reminder and subscription message goes as one text, never both, so nothing is paid for twice.
            The other channel is used only if the first didn't send: the patient hasn't agreed to WhatsApp, the
            template is off, or the provider returned an error such as no balance. Applies to the whole platform.
          </p>
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {!switches && !error && <p className="text-secondary-500">Loading…</p>}

        {switches && (
          <section className="rounded-xl border border-secondary-200 bg-white p-4 space-y-3">
            <h2 className="text-lg font-semibold text-secondary-900">Send each message by</h2>
            <div className="space-y-2">
              {OPTIONS.map((o) => (
                <label key={o.value} className={`flex items-start gap-3 rounded-lg border p-3 normal-case font-sans tracking-normal cursor-pointer ${preferred === o.value ? "border-emerald-300 bg-emerald-50" : "border-secondary-200"}`}>
                  <input type="radio" name="preferred-channel" className="mt-1 h-4 w-4" checked={preferred === o.value}
                    disabled={busy} onChange={() => choose(o.value)} />
                  <span>
                    <span className="block text-sm font-semibold text-secondary-900">{o.label}</span>
                    <span className="block text-xs text-secondary-600 mt-0.5">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {(!switches.sms || !switches.whatsapp) && (
              <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                {!switches.sms && !switches.whatsapp ? "SMS and WhatsApp are both" : !switches.sms ? "SMS is" : "WhatsApp is"} switched
                off for the whole platform (on the SMS / WhatsApp Templates pages), so {!switches.sms && !switches.whatsapp ? "no text goes out" : "messages only go on the other channel"}.
              </p>
            )}
          </section>
        )}

        {data?.messages && <MessageMap data={data} />}

        <section className="rounded-xl border border-secondary-200 bg-white p-4 space-y-2 text-sm text-secondary-700">
          <h2 className="text-lg font-semibold text-secondary-900">What this doesn't change</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Sign-in codes go by SMS, or by WhatsApp when the person asks for it, falling back to SMS.</li>
            <li>Some messages only ever use one channel: homework, plans and check-in reminders never go by SMS, and payment receipts never by WhatsApp.</li>
            <li>A message held back by the sending limits isn't retried on the other channel.</li>
            <li>Clinics only choose whether to text a patient about something (Settings → Patient alerts), not which channel.</li>
          </ul>
        </section>
      </div>
    </DashboardLayout>
  );
}
